import {
  Badge,
  BadgeColor,
  BadgeContainer,
} from '@delmonicos/shared-design-system-react/src/components/badge/badge';
import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import {
  Dialog,
  DialogPosition,
  DialogSize,
} from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import {
  ResourceStatus,
  withProtectedResource,
} from '@delmonicos/shared-design-system-react/src/components/protected-resources/with-protected-resource.helper';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { GetCustomersPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/customers/models/get-customers-paginated.model';
import {
  hasAccessTo,
  safeHasAccessTo,
} from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { transformPaginatedResponse } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/transform-api-paginated-response/transform-api-response';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import {
  OnChangeFn,
  SortingState,
  functionalUpdate,
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { permissions } from '@app/core/authentication/authentication.slice';
import { getState } from '@app/core/helper/get-state';
import { useAppSelector } from '@app/core/hooks';
import { useCpmsBoGetCustomersPaginatedQuery } from '@app/core/service/cpms-bo-customer.service';
import { useSetPageActionContext } from '@app/shared/layout/page-layout/page-layout-action.context-provider';
import {
  createColumnOrderChangeHandler,
  createHideColumnHandler,
} from '@shared/list-options/column-options.helper';

import { CustomerCreateController } from '../customer-create/customer-create.controller';
import { CustomerListFilterSearchController } from '../customer-list-filter-search/customer-list-filter-search.controller';
import { CustomerListFilterStateController } from '../customer-list-filter-state/customer-list-filter-state.controller';
import { CustomerListFilterController } from '../customer-list-filter/customer-list-filter.controller';
import { CustomerListOptionsController } from '../customer-list-options/customer-list-options.controller';
import {
  CustomerListContextProvider,
  useCustomerListContext,
} from '../customer.context';
import {
  CUSTOMER_LIST_FILTER_NAME_LIST,
  CUSTOMER_LIST_SORT_FIELD,
} from './customer-list.config';
import * as css from './customer-list.module.scss';
import { CustomerListView } from './customer-list.view';

export const CustomerListController = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const {
    getPaginatedQueryParameters,
    setGetPaginatedQueryParameters,
    getPaginatedLimitList,
    columnOptions,
    setColumnOptions,
  } = useCustomerListContext();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  // ACTIONS ///////////////////////////////////////////////////////////////////

  const [actionCreateIsDialogOpen, setActionCreateIsDialogOpen] =
    useState(false);

  const [actionFilterIsDialogOpen, setActionFilterIsDialogOpen] =
    useState(false);

  // DATA FETCHING /////////////////////////////////////////////////////////////

  const queryState = useCpmsBoGetCustomersPaginatedQuery(
    getPaginatedQueryParameters,
  );

  const paginationState = useMemo(
    () =>
      getState({
        isLoading: queryState.isLoading,
        isError: queryState.isError,
        isFetching: queryState.isFetching,
      }),
    [queryState.isLoading, queryState.isError, queryState.isFetching],
  );

  // TODO: Should be moved in filter-search component
  //   if (debouncedTextToSearch && debouncedTextToSearch.trim().length > 0) {
  //     params.textToSearch = debouncedTextToSearch.trim() || '';
  //   }

  // TODO: Should be move in dedicated component
  const filterNameActiveList = CUSTOMER_LIST_FILTER_NAME_LIST.filter(
    (filterName) => getPaginatedQueryParameters[filterName] !== undefined,
  );

  const filerActiveHiddenCount = Math.max(
    0,
    filterNameActiveList.reduce((count, filterName) => {
      const filterValue = getPaginatedQueryParameters[filterName] as any;

      if (Array.isArray(filterValue)) {
        return count + filterValue.length;
      }

      return count + 1;
    }, 0) - 3,
  );

  // PAGE TITLE ////////////////////////////////////////////////////////////////

  // Nothing to do

  // PAGE ACTIONS //////////////////////////////////////////////////////////////

  const setPageAction = useSetPageActionContext();

  useEffect(() => {
    const actions = [];

    if (
      userPermissions &&
      hasAccessTo(userPermissions, {
        resourceName: Resource.CUSTOMERS,
        action: ActionsByResource.CUSTOMERS.CREATE,
      })
    ) {
      actions.push(
        <ButtonIcon
          key="create"
          icon="plus"
          onClick={() => setActionCreateIsDialogOpen(true)}
        />,
      );
    }

    setPageAction(actions);
  }, [setPageAction, userPermissions]);

  // PAGE BREADCRUMB ///////////////////////////////////////////////////////////

  // Nothing to do

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handlePaginationPageChange = (paginationPageNext: number) => {
    setGetPaginatedQueryParameters((current) => ({
      ...current,
      offset: (paginationPageNext - 1) * current.limit,
    }));
  };

  const handlePaginationLimitChange = (paginationLimitNext: number) => {
    setGetPaginatedQueryParameters((current) => ({
      ...current,
      limit: paginationLimitNext,
      offset: 0,
    }));
  };

  const handlePaginationSortingChange: OnChangeFn<SortingState> = (
    sortingState,
  ) => {
    setGetPaginatedQueryParameters((current) => {
      const paginationSorting0 = functionalUpdate(sortingState, [
        {
          desc: current.sortOrder === -1,
          id: current.sortField ?? CUSTOMER_LIST_SORT_FIELD,
        },
      ]).at(0);

      return {
        ...current,
        sortField:
          paginationSorting0?.id as GetCustomersPaginatedModel['sortField'],
        sortOrder: paginationSorting0?.desc ? -1 : 1,
      };
    });
  };

  const handleHideColumn = createHideColumnHandler(setColumnOptions);
  const handleColumnOrderChange =
    createColumnOrderChangeHandler(setColumnOptions);

  // RENDER ////////////////////////////////////////////////////////////////////

  const { Component: CustomerListViewProtected, status: resourceStatus } =
    withProtectedResource(CustomerListView, queryState, {
      isAuthorized: () =>
        safeHasAccessTo(userPermissions, {
          resourceName: Resource.CUSTOMERS,
          action: ActionsByResource.CUSTOMERS.READ,
        }),
      transformDataToResource: transformPaginatedResponse,
    })({
      paginationLimit: getPaginatedQueryParameters.limit,
      paginationLimitList: getPaginatedLimitList,
      paginationState,
      paginationSorting: [
        {
          desc: getPaginatedQueryParameters.sortOrder === -1,
          id: getPaginatedQueryParameters.sortField ?? CUSTOMER_LIST_SORT_FIELD,
        },
      ],
      columnVisibility: columnOptions.columnVisibility,
      columnOrder: columnOptions.columnOrder,
      renderColumnSettings: () => <CustomerListOptionsController />,
      onPaginationLimitChange: handlePaginationLimitChange,
      onPaginationPageChange: handlePaginationPageChange,
      onPaginationSortingChange: handlePaginationSortingChange,
      onHideColumn: handleHideColumn,
      onColumnOrderChange: handleColumnOrderChange,
    });

  return (
    <Stack flex={1} className={css.listContainer} direction="column">
      {resourceStatus === ResourceStatus.SUCCESS && (
        <Stack direction="row" justifyContent="space-between" gap={GapEnum.XL}>
          <Stack flex={1}>
            <CustomerListFilterSearchController
              onCancel={() => {}}
              onSuccess={() => {}}
            />
          </Stack>
          <CustomerListFilterStateController />
          <Stack justifyContent="end" gap={GapEnum.M}>
            <BadgeContainer
              badge={
                filerActiveHiddenCount > 0 && (
                  <Badge color={BadgeColor.Secondary}>
                    {`+${filerActiveHiddenCount}`}
                  </Badge>
                )
              }>
              <ButtonIcon
                icon="filter-funnel-01"
                onClick={() => setActionFilterIsDialogOpen(true)}
              />
            </BadgeContainer>
          </Stack>
        </Stack>
      )}
      <Stack className={css.tableContainer}>{CustomerListViewProtected}</Stack>
      {resourceStatus === ResourceStatus.SUCCESS && (
        <>
          <Dialog
            open={actionCreateIsDialogOpen}
            onClose={() => setActionCreateIsDialogOpen(false)}
            confirmClose
            height="100%"
            size={DialogSize.LARGE}>
            <Dialog.Header
              icon="dataflow-04"
              onClose={() => setActionCreateIsDialogOpen(false)}
              title={t('$.customer-create.title')}
            />
            <Dialog.Content>
              <CustomerCreateController
                onCancel={() => setActionCreateIsDialogOpen(false)}
                onSuccess={() => setActionCreateIsDialogOpen(false)}
              />
            </Dialog.Content>
          </Dialog>
          <Dialog
            open={actionFilterIsDialogOpen}
            onClose={() => setActionFilterIsDialogOpen(false)}
            position={DialogPosition.RIGHT}>
            <Dialog.Header
              icon="filter-funnel-01"
              onClose={() => setActionFilterIsDialogOpen(false)}
              title={t('$.customer-list-filter.title')}
            />
            <Dialog.Content>
              <CustomerListFilterController
                onCancel={() => setActionFilterIsDialogOpen(false)}
                onSuccess={() => setActionFilterIsDialogOpen(false)}
              />
            </Dialog.Content>
          </Dialog>
        </>
      )}
    </Stack>
  );
};

export const CustomerListControllerWithContext = (): React.JSX.Element => {
  return (
    <CustomerListContextProvider>
      <CustomerListController />
    </CustomerListContextProvider>
  );
};
